import './call.css';
import type { GroupCallState } from '../../contexts/GroupCallContext';
import type { RecordingStatus } from '../../api/callRecording';
import {
  RecBadge, RecordingNotice, recordingUi, showsRecordingNotice, StartRecordingButton, useStartRecording,
} from './RecordingIndicators';
import { CameraControl, VideoTrackView } from './CallVideo';
import { RemoteScreenView, ScreenShareBanner, ScreenShareControl } from './ScreenShare';

function initial(name: string | null): string { return (name || '?').charAt(0).toUpperCase(); }

const PhoneIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92Z" /></svg>);
const PhoneOffIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91" /><line x1="23" y1="1" x2="1" y2="23" /></svg>);
const MicIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3Z" /><path d="M19 10v2a7 7 0 0 1-14 0v-2" /><line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" /></svg>);
const MicOffIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="1" y1="1" x2="23" y2="23" /><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" /><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" /><line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" /></svg>);
const UserPlusIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>);

interface Props {
  state: GroupCallState;
  /** Server-side recording status of this call, if it has one. */
  recordingStatus?: RecordingStatus | null;
  /** `manual` ("During call"): records only once a participant starts it. */
  recordingStartMode?: 'auto' | 'manual' | null;
  /** Viewer-relative (server): may press Start recording now. */
  canStartRecording?: boolean;
  onStartRecording?: () => void | Promise<unknown>;
  onAccept: () => void;
  onDecline: () => void;
  onLeave: () => void;
  onToggleMute: () => void;
  /** Camera toggle; given once this side is in the room. */
  onToggleVideo?: () => void;
  /** Share/stop this side's screen; given once in the room, in a browser that can capture it. */
  onToggleScreenShare?: () => void;
  onAddParticipant?: () => void;
}

export default function GroupCallScreen({
  state, recordingStatus = null, recordingStartMode = null, canStartRecording = false, onStartRecording,
  onAccept, onDecline, onLeave, onToggleMute, onToggleVideo, onToggleScreenShare, onAddParticipant,
}: Props) {
  const { status, conversationName, starterName, participants, muted } = state;
  const selfView = status !== 'incoming' && status !== 'ended' ? state.localVideoTrack ?? null : null;
  const cameraOn = !!state.localVideoTrack;
  const isIncoming = status === 'incoming';
  const recordingLive = recordingStatus === 'recording' && status !== 'ended';
  // A group caller is "connected" while the others still ring, so the notice
  // stays until the recording is live (then the REC badge takes over).
  // A pending "During call" recording: "may be recorded" before connecting,
  // then the Start recording button (for whoever may press it) instead.
  const recUi = recordingUi(recordingStatus, recordingStartMode, status === 'connected', canStartRecording && !!onStartRecording);
  const recordingNotice = status !== 'ended' && showsRecordingNotice(recordingStatus)
    && !(status === 'connected' && recordingStatus === 'recording') && !recUi.hideNotice;
  const startRecording = useStartRecording(onStartRecording);
  const title = conversationName || 'Group call';
  const live = !isIncoming && status !== 'ended';
  // One shared screen at a time: the first participant sharing.
  const sharer = live ? participants.find((p) => p.screenTrack) ?? null : null;

  return (
    <div className="cs-overlay" role="dialog" aria-label={isIncoming ? 'Incoming group call' : 'Group call'}>
      <div className={`cs-window${sharer ? ' cs-window--screen' : ''}`}>
        {selfView && <VideoTrackView track={selfView} className="cs-selfview" mirrored />}
        <div className="cs-card">
          <div className="cs-name">{title}</div>
          <div className="cs-status">
            {isIncoming ? <>{starterName || 'Someone'} started a group call</>
              : status === 'connecting' ? <>Connecting<span className="cs-dots" /></>
              : status === 'ended' ? <>Call ended</>
              : <>{participants.length + 1} in call</>}
            {recordingLive && <RecBadge />}
          </div>
          {recordingNotice && <RecordingNotice mode={recUi.mode} />}
          {recUi.showStart && <StartRecordingButton onStart={startRecording.start} busy={startRecording.busy} />}
          {live && state.screenSharing && <ScreenShareBanner onStop={onToggleScreenShare} />}

          {!isIncoming && (
            <div className="cs-grid">
              {sharer?.screenTrack && (
                <RemoteScreenView track={sharer.screenTrack} name={sharer.name || sharer.userId} className="cs-tile-screen" />
              )}
              {participants.map((p) => {
                const wrapState = p.connState === 'connected'
                  ? (p.speaking ? 'is-speaking' : 'is-connected')
                  : 'is-ringing';
                return (
                <div key={p.userId} className="cs-tile">
                  {p.videoTrack ? (
                    <VideoTrackView
                      track={p.videoTrack}
                      className={`cs-tile-video${p.speaking ? ' is-speaking' : ''}`}
                    />
                  ) : (
                    <div className={`cs-avatar-wrap ${wrapState}`}>
                      <span className="cs-ring" />
                      <span className="cs-ring" />
                      <span className="cs-ring" />
                      {p.avatarUrl
                        ? <img src={p.avatarUrl} alt="" className="cs-avatar" />
                        : <span className="cs-avatar--fallback">{initial(p.name)}</span>}
                    </div>
                  )}
                  <div className="cs-tile-name">{p.name || p.userId}</div>
                  <div className="cs-tile-state cs-status">
                    {p.connState === 'connected' ? 'Connected' : 'Connecting…'}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="cs-controls">
          {isIncoming ? (
            <>
              <div className="cs-control">
                <button type="button" className="cs-btn cs-btn--decline" onClick={onDecline} aria-label="Decline call"><PhoneOffIcon /></button>
                <span className="cs-control-label">Decline</span>
              </div>
              <div className="cs-control">
                <button type="button" className="cs-btn cs-btn--accept" onClick={onAccept} aria-label="Accept call"><PhoneIcon /></button>
                <span className="cs-control-label">Accept</span>
              </div>
            </>
          ) : status !== 'ended' ? (
            <>
              <div className="cs-control">
                <button type="button" className={`cs-btn${muted ? ' cs-btn--muted' : ''}`} onClick={onToggleMute} aria-label={muted ? 'Unmute' : 'Mute'}>
                  {muted ? <MicOffIcon /> : <MicIcon />}
                </button>
                <span className="cs-control-label">{muted ? 'Unmute' : 'Mute'}</span>
              </div>
              {onToggleVideo && <CameraControl on={cameraOn} busy={!!state.cameraBusy} onToggle={onToggleVideo} />}
              {onToggleScreenShare && (
                <ScreenShareControl sharing={!!state.screenSharing} busy={!!state.screenShareBusy} onToggle={onToggleScreenShare} />
              )}
              {onAddParticipant && (
                <div className="cs-control">
                  <button type="button" className="cs-btn" onClick={onAddParticipant} aria-label="Add participant"><UserPlusIcon /></button>
                  <span className="cs-control-label">Add</span>
                </div>
              )}
              <div className="cs-control">
                <button type="button" className="cs-btn cs-btn--end" onClick={onLeave} aria-label="Leave call"><PhoneOffIcon /></button>
                <span className="cs-control-label">Leave</span>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
